import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Loader2, BellRing, ShieldCheck, Check } from "lucide-react";
import { describeError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ALERT_CATEGORIES, getEnrollment, startEnrollment, verifyEnrollment, dismissEnrollment } from "@/lib/alertsApi";

const INK = "#141412";

// Broadcast so the Topbar indicator refreshes without its own fetch.
const emitStatus = (status) => window.dispatchEvent(new CustomEvent("revq:alerts-status", { detail: { status } }));

export default function AlertsNudge() {
  const { token, user } = useAuth();
  const [status, setStatus] = useState(null);     // unenrolled | pending | verified | dismissed | failed
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("intro");       // intro | otp
  const [picked, setPicked] = useState(() => ALERT_CATEGORIES.map((c) => c.key)); // default all
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [resendIn, setResendIn] = useState(0);

  const email = user?.email || "";

  const refresh = useCallback(async () => {
    if (!sessionStorage.getItem("revq.token")) return;
    try {
      const d = await getEnrollment();
      setStatus(d.status);
      emitStatus(d.status);
      if (d.categories?.length) setPicked(d.categories);
      // Auto-nudge once per session only when truly unenrolled — and never on top
      // of the identity-verification / BAA wall.
      const walled = document.querySelector('[data-testid="verify-gate"], [data-testid="baa-gate"]');
      if (d.status === "unenrolled" && !sessionStorage.getItem("revq.alerts.nudged") && !walled) {
        sessionStorage.setItem("revq.alerts.nudged", "1");
        setOpen(true);
      }
    } catch { /* non-blocking */ }
  }, []);

  useEffect(() => { if (token) refresh(); }, [token, refresh]);

  // Let the Topbar indicator (or anything) open enrollment on demand.
  useEffect(() => {
    const onOpen = () => { setStep("intro"); setErr(""); setOtp(""); setOpen(true); };
    window.addEventListener("revq:open-alerts", onOpen);
    return () => window.removeEventListener("revq:open-alerts", onOpen);
  }, []);

  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const t = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  if (!token || !open) return null;

  const toggle = (k) => setPicked((p) => (p.includes(k) ? p.filter((x) => x !== k) : [...p, k]));

  const sendCode = async () => {
    setBusy(true); setErr("");
    try {
      const d = await startEnrollment();
      setResendIn(d.resend_in || 60);
      setStep("otp");
      setStatus("pending"); emitStatus("pending");
      toast.success(`Code sent to ${email}`);
    } catch (e) { setErr(describeError(e)); } finally { setBusy(false); }
  };

  const verify = async () => {
    setBusy(true); setErr("");
    try {
      await verifyEnrollment(otp.trim(), picked);
      setStatus("verified"); emitStatus("verified");
      setOpen(false);
      toast.success("Email alerts turned on ✓");
    } catch (e) { setErr(describeError(e)); } finally { setBusy(false); }
  };

  const maybeLater = async () => {
    setOpen(false);
    try { await dismissEnrollment(); setStatus("dismissed"); emitStatus("dismissed"); } catch { /* ignore */ }
  };

  const close = () => setOpen(false);

  return (
    <div data-testid="alerts-nudge" style={{ position: "fixed", inset: 0, zIndex: 9100, background: "rgba(20,20,18,.45)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
      onClick={(e) => { if (e.target === e.currentTarget) close(); }}>
      <div style={{ width: "min(560px, 96vw)", maxHeight: "92vh", overflowY: "auto", background: "#fff", border: "1px solid #E6E6E4", borderRadius: 18, boxShadow: "0 30px 80px rgba(20,20,18,.28)" }}>
        {/* header */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "18px 22px", borderBottom: "1px solid #EEEEEC" }}>
          <span style={{ width: 34, height: 34, borderRadius: 9, background: INK, display: "inline-flex", alignItems: "center", justifyContent: "center", color: "#fff" }}><BellRing size={17} /></span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: INK }}>Turn on email alerts</div>
            <div style={{ fontSize: 12, color: "#6C6C69" }}>Stay in the loop on processing, batches and billing — optional, your choice.</div>
          </div>
          <button onClick={close} data-testid="alerts-close" aria-label="Close" style={{ color: "#9B9B97", background: "transparent", border: 0, cursor: "pointer" }}><X size={18} /></button>
        </div>

        <div style={{ padding: "18px 22px 20px" }}>
          {err ? <div data-testid="alerts-error" style={{ fontSize: 12.5, color: "#B3261E", background: "#FDEDEC", border: "1px solid #F3D3D0", borderRadius: 9, padding: "9px 11px", marginBottom: 14 }}>{err}</div> : null}

          {step === "intro" ? (
            <>
              <div style={{ fontSize: 13, fontWeight: 700, color: INK, marginBottom: 10 }}>Choose what you'd like to hear about</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                {ALERT_CATEGORIES.map((c) => {
                  const on = picked.includes(c.key);
                  return (
                    <button key={c.key} onClick={() => toggle(c.key)} data-testid={`alerts-cat-${c.key}`} aria-pressed={on}
                      style={{ textAlign: "left", border: `1.5px solid ${on ? INK : "#E6E6E4"}`, background: on ? "#FAFAFA" : "#fff", borderRadius: 12, padding: "10px 12px", cursor: "pointer" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ width: 16, height: 16, borderRadius: 5, border: `1.5px solid ${on ? INK : "#C9C9C5"}`, background: on ? INK : "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center", flex: "none" }}>{on ? <Check size={11} color="#fff" /> : null}</span>
                        <span style={{ fontSize: 12.5, fontWeight: 700, color: INK }}>{c.label}</span>
                      </div>
                      <div style={{ fontSize: 10.5, color: "#8A8A86", marginTop: 4, lineHeight: 1.4, paddingLeft: 24 }}>{c.desc}</div>
                    </button>
                  );
                })}
              </div>

              <div style={{ marginTop: 14, fontSize: 12, lineHeight: 1.55, color: "#4A4A46", background: "#F7F7F6", borderRadius: 10, padding: "11px 13px" }}>
                Alerts are sent only to your logged-in email <b>{email}</b> and this can't be changed. You'll receive them from <b>alerts@notifications.ariaone.ai</b> and/or <b>info@notifications.ariaone.ai</b>.
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 18 }}>
                <button onClick={maybeLater} data-testid="alerts-later" style={{ fontSize: 12.5, fontWeight: 700, color: "#6C6C69", background: "transparent", border: 0, cursor: "pointer" }}>Maybe later</button>
                <span style={{ flex: 1 }} />
                <button onClick={sendCode} disabled={busy || picked.length === 0} data-testid="alerts-sendcode" style={btn(!busy && picked.length > 0)}>
                  {busy ? <><Loader2 size={14} className="animate-spin" /> Sending…</> : "Send me a code"}
                </button>
              </div>
            </>
          ) : (
            <>
              <div style={{ fontSize: 14, fontWeight: 800, color: INK }}>Enter your 6-digit code</div>
              <p style={{ fontSize: 12.5, lineHeight: 1.55, color: "#4A4A46", margin: "8px 0 14px" }}>We emailed a code to <b>{email}</b>. Enter it below to confirm.</p>
              <input data-testid="alerts-otp-input" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" autoFocus
                placeholder="------" maxLength={6}
                style={{ width: "100%", boxSizing: "border-box", textAlign: "center", letterSpacing: "12px", fontSize: 26, fontWeight: 800, fontFamily: "ui-monospace,Menlo,monospace", padding: "12px", border: "1px solid #E2E2DF", borderRadius: 12, color: INK, outline: "none" }} />
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 16 }}>
                <button onClick={sendCode} disabled={busy || resendIn > 0} data-testid="alerts-resend" style={{ fontSize: 12.5, fontWeight: 700, color: resendIn > 0 ? "#B9B9B5" : "#6C6C69", background: "transparent", border: 0, cursor: resendIn > 0 ? "default" : "pointer" }}>
                  {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend code"}
                </button>
                <span style={{ flex: 1 }} />
                <button onClick={verify} disabled={busy || otp.length !== 6} data-testid="alerts-verify" style={btn(!busy && otp.length === 6)}>
                  {busy ? <><Loader2 size={14} className="animate-spin" /> Confirming…</> : "Confirm & turn on"}
                </button>
              </div>
            </>
          )}
        </div>

        <div style={{ padding: "0 22px 18px", display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "#9B9B97" }}>
          <ShieldCheck size={12} /> You can change or turn these off anytime from the alerts indicator.
        </div>
      </div>
    </div>
  );
}

function btn(on) {
  return { display: "inline-flex", alignItems: "center", gap: 8, background: on ? INK : "#E4E4E1", color: on ? "#fff" : "#9B9B97", border: 0, borderRadius: 999, padding: "11px 20px", fontWeight: 800, fontSize: 13.5, cursor: on ? "pointer" : "default" };
}
